import Mustache from 'mustache';
import { useMemo, useRef } from 'react';
import { createFilter } from 'react-select';
import { useFormApi, useFormState } from 'informed';
import { localeLanguageCodes, localeNames } from 'shared-locales';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Button, FlagIcon, LanguageIcon, Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Checkbox,
  FormGroup,
  LanguageVariants,
  RichText,
  Select,
  Switch
} from 'components/common/form';
import { Popover } from 'components/common/popover';

import type { Editor } from '@tiptap/core';
import type {
  EmailMessagePlaceholder,
  EmailMessageTemplate,
  EmailTemplateData,
  Locales
} from 'shared-types';

type FormValues = {
  email: Pick<
    EmailMessageTemplate,
    | 'buttonEnabled'
    | 'buttonLink'
    | 'buttonText'
    | 'content'
    | 'contentAfterButton'
    | 'footnote'
    | 'heading'
    | 'language'
    | 'subject'
    | 'supportedTranslations'
  >;
};

type Props = {
  id: string;
  placeholders?: { [key: string]: EmailMessagePlaceholder };
  style?: React.CSSProperties;
} & (
  | {
      availableTemplates?: EmailTemplateData[];
      enableTranslations?: never;
    }
  | {
      availableTemplates?: never;
      enableTranslations?: boolean;
    }
);

const EmailMessageEditor = ({
  availableTemplates,
  enableTranslations = false,
  id,
  placeholders = undefined,
  style
}: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  const activeEditorRef = useRef<Editor>(undefined);

  const formApi = useFormApi<FormValues>();
  const formState = useFormState<FormValues>();

  const placeholderValues: { [key: string]: string } = {};
  const templateTags: (EmailMessagePlaceholder & { key: string })[] = [];

  Object.keys(placeholders || {}).forEach((key) => {
    placeholderValues[key] = placeholders[key].value || `{{${key}}}`;
    templateTags.push({
      key,
      ...placeholders[key]
    });
  });

  const getRenderedValue = (value: string) => {
    try {
      const renderedValue = Mustache.render(value || '', placeholderValues);
      return renderedValue;
    } catch (error) {
      return value;
    }
  };

  const handleFocus = (editor: Editor) => {
    activeEditorRef.current = editor;
  };

  const handleTemplateSelected = (identifier: string) => {
    const selectedTemplate = availableTemplates.find(
      (template) => template.identifier === identifier
    );

    if (!selectedTemplate) return;

    formApi.setValue('email.buttonLink', selectedTemplate.email.buttonLink);
    formApi.setValue('email.buttonText', selectedTemplate.email.buttonText);
    formApi.setValue('email.content', selectedTemplate.email.content);
    formApi.setValue(
      'email.contentAfterButton',
      selectedTemplate.email.contentAfterButton
    );
    formApi.setValue('email.footnote', selectedTemplate.email.footnote);
    formApi.setValue('email.heading', selectedTemplate.email.heading);
    formApi.setValue('email.subject', selectedTemplate.email.subject);
  };

  const handleTemplateTagAdd = (templateTagKey: string) => {
    if (!activeEditorRef.current) return;

    activeEditorRef.current
      ?.chain()
      .focus()
      .insertContent(`{{${templateTagKey}}}`)
      .run();
  };

  const validateRequired = (value: string) => {
    return !value?.toString().trim() ? messages.commonFieldRequired : undefined;
  };

  const showButton =
    formState.values?.email?.buttonEnabled &&
    formState.values?.email?.buttonLink &&
    formState.values?.email?.buttonText;

  const buttonText = useMemo(
    () => getRenderedValue(formState.values?.email?.buttonText),
    [formState.values?.email?.buttonText]
  );

  const language = formState.values?.email?.language;
  const supportedTranslations =
    formState.values?.email?.supportedTranslations || {};

  const supportedTranslationLocales = Object.keys(supportedTranslations).filter(
    (translationLocale) =>
      translationLocale !== language && supportedTranslations[translationLocale]
  );

  const showTranslationInputs = Boolean(supportedTranslationLocales.length);

  return (
    <div className="email-message-editor" style={style}>
      <div className="header">
        <div className="actions">
          {enableTranslations ? (
            <>
              <div className="language">
                <div className="item">
                  <label className="item-label">
                    {messages.commonLanguage}
                  </label>

                  <div className="item-value">
                    <div className="languages">
                      <LanguageIcon language={language} size="large" />

                      {supportedTranslationLocales.map((locale) => (
                        <LanguageIcon
                          key={locale}
                          language={locale as Locales}
                          size="large"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="language-settings">
                <Popover
                  align="end"
                  content={
                    <div className="language-settings-content">
                      <FormGroup label={messages.commonLanguage}>
                        <Select
                          field="email.language"
                          id={`email-language-${id}`}
                          maxMenuHeight={200}
                          onValueChange={() => {}}
                          options={Object.keys(localeNames)
                            .filter((key: Locales) =>
                              serverConfig.supportedLanguages.includes(key)
                            )
                            .sort((a, b) => {
                              // Русский is hardcoded as an exception to be treated as the word "Russian"
                              // when sorting alphabetically, so that it doesn't go to the bottom of the list
                              const nameA =
                                localeNames[a] === 'Русский'
                                  ? 'Russian'
                                  : localeNames[a];
                              const nameB =
                                localeNames[b] === 'Русский'
                                  ? 'Russian'
                                  : localeNames[b];

                              return nameA.localeCompare(nameB);
                            })
                            .map((key) => ({
                              label: (
                                <span>
                                  <FlagIcon
                                    lang={localeLanguageCodes[key]}
                                    style={{
                                      marginRight: 7,
                                      marginTop: -3
                                    }}
                                  />
                                  {localeNames[key]}
                                </span>
                              ),
                              value: key
                            }))}
                          showLabelForSelectedElement={true}
                          usePortal={true}
                        />
                      </FormGroup>

                      <FormGroup label={messages.settingsLanguageVariants}>
                        {Object.keys(localeNames)
                          .filter(
                            (locale) =>
                              locale !== formState.values?.email?.language
                          )
                          .map((locale) => (
                            <Checkbox
                              key={locale}
                              field={`email.supportedTranslations['${locale}']`}
                              id={`email-supported-translations-${locale}`}
                              label={
                                <span>
                                  <FlagIcon
                                    lang={localeLanguageCodes[locale]}
                                    size="small"
                                    style={{ margin: '-2px 5px 0' }}
                                  />
                                  {localeNames[locale]}
                                </span>
                              }
                              style={{ marginBottom: 10 }}
                            />
                          ))}
                      </FormGroup>
                    </div>
                  }
                  forceMount={true}
                  side="bottom"
                >
                  {(setIsOpen) => (
                    <Button
                      category="light"
                      icon="cog"
                      onClick={() => setIsOpen(true)}
                      size="header"
                    />
                  )}
                </Popover>
              </div>
            </>
          ) : availableTemplates?.length ? (
            <div className="select-template">
              <div className="item">
                <label className="item-label">
                  {messages.emailMessageTemplate}
                </label>
                <div className="item-value">
                  <div className="select-template-input">
                    <Select
                      dropdownStyles={true}
                      field="email.selectedTemplateIdentifier"
                      filterOption={createFilter({
                        stringify: (option) =>
                          availableTemplates?.find(
                            (template) => template.identifier === option.value
                          )?.title
                      })}
                      id={`email-selected-template-identifier-${id}`}
                      isSearchable={true}
                      menuAlign="right"
                      menuWidth={300}
                      onValueChange={handleTemplateSelected}
                      options={availableTemplates.map((availableTemplate) => ({
                        helpText: availableTemplate.isBuiltInTemplate
                          ? undefined
                          : messages.emailCustomTemplate,
                        label: (
                          <div style={{ display: 'inline-flex', gap: 5 }}>
                            <div>
                              <LanguageIcon
                                iconStyle={{
                                  fontSize: '1.4rem',
                                  position: 'relative',
                                  top: 2
                                }}
                                language={availableTemplate.language}
                              />
                            </div>
                            <div style={{ fontSize: '1.4rem' }}>
                              {availableTemplate.title}
                            </div>
                          </div>
                        ),
                        value: availableTemplate.identifier
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="subject">
          <div className="item">
            <label className="item-label">{messages.commonSubject}</label>
            <div
              className={`item-value ${
                showTranslationInputs ? 'item-value-with-translations' : ''
              }`}
            >
              <RichText
                field="email.subject"
                formatting={false}
                onFocus={handleFocus}
                placeholder={messages.emailSubject}
                singleLine={true}
                singleLineRemoveParagraph={true}
                templateTags={templateTags}
                templateTagsIdentifier="email-message-editor-subject"
                validate={validateRequired}
                validateOnChange={true}
                withBorder={true}
              />
            </div>
          </div>

          {showTranslationInputs ? (
            <div className="item-translations">
              <LanguageVariants
                customProps={{
                  formatting: false,
                  onFocus: handleFocus,
                  singleLine: true,
                  singleLineRemoveParagraph: true,
                  templateTags: templateTags,
                  templateTagsIdentifier:
                    'email-message-editor-subject-translations'
                }}
                field="email.subjectTranslations"
                supportedLocales={supportedTranslationLocales}
                type="richtext"
              />
            </div>
          ) : null}
        </div>
      </div>

      <div className="message">
        <div className="message-inner">
          <div className="message-heading">
            <div className="message-rich-text">
              <RichText
                field="email.heading"
                formatting={false}
                onFocus={handleFocus}
                placeholder={messages.emailHeading}
                singleLine={true}
                singleLineRemoveParagraph={true}
                templateTags={templateTags}
                templateTagsIdentifier="email-message-editor-heading"
              />
            </div>

            {showTranslationInputs ? (
              <div className="message-translations">
                <LanguageVariants
                  customProps={{
                    formatting: false,
                    onFocus: handleFocus,
                    singleLine: true,
                    singleLineRemoveParagraph: true,
                    templateTags: templateTags,
                    templateTagsIdentifier:
                      'email-message-editor-heading-translations'
                  }}
                  field="email.headingTranslations"
                  supportedLocales={supportedTranslationLocales}
                  type="richtext"
                />
              </div>
            ) : null}
          </div>

          <div className="message-content">
            <div className="message-rich-text">
              <RichText
                field="email.content"
                onFocus={handleFocus}
                placeholder={messages.emailContent}
                templateTags={templateTags}
                templateTagsIdentifier="email-message-editor-content"
                validate={validateRequired}
                validateOnChange={true}
              />
            </div>

            {showTranslationInputs ? (
              <div className="message-translations">
                <LanguageVariants
                  customProps={{
                    onFocus: handleFocus,
                    templateTags: templateTags,
                    templateTagsIdentifier:
                      'email-message-editor-content-translations'
                  }}
                  field="email.contentTranslations"
                  supportedLocales={supportedTranslationLocales}
                  type="richtext"
                />
              </div>
            ) : null}
          </div>

          {showButton ? (
            <>
              <div className="message-button-container">
                <div className="message-button">{buttonText}</div>
              </div>

              <div className="message-content-after-button">
                <div className="message-rich-text">
                  <RichText
                    field="email.contentAfterButton"
                    onFocus={handleFocus}
                    placeholder={messages.emailContentAfterButton}
                    templateTags={templateTags}
                    templateTagsIdentifier="email-message-editor-content-after-button"
                  />
                </div>

                {showTranslationInputs ? (
                  <div className="message-translations">
                    <LanguageVariants
                      customProps={{
                        onFocus: handleFocus,
                        templateTags: templateTags,
                        templateTagsIdentifier:
                          'email-message-editor-content-after-button-translations'
                      }}
                      field="email.contentAfterButtonTranslations"
                      supportedLocales={supportedTranslationLocales}
                      type="richtext"
                    />
                  </div>
                ) : null}
              </div>
            </>
          ) : null}

          <div className="message-content-footnote">
            <div className="message-rich-text">
              <RichText
                field="email.footnote"
                onFocus={handleFocus}
                singleLine={true}
                singleLineRemoveParagraph={true}
                templateTags={templateTags}
                templateTagsIdentifier="email-message-editor-footnote"
              />
            </div>

            {showTranslationInputs ? (
              <div className="message-translations">
                <LanguageVariants
                  customProps={{
                    onFocus: handleFocus,
                    singleLine: true,
                    singleLineRemoveParagraph: true,
                    templateTags: templateTags,
                    templateTagsIdentifier:
                      'email-message-editor-footnote-translations'
                  }}
                  field="email.footnoteTranslations"
                  supportedLocales={supportedTranslationLocales}
                  type="richtext"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="settings-button">
        <div className="settings-button-enabled">
          <div className="item">
            <Switch
              field="email.buttonEnabled"
              id="email-button-enabled"
              labelBefore={messages.commonShowButton}
              labelStyle={{
                fontWeight: 600,
                marginRight: 10,
                whiteSpace: 'nowrap'
              }}
            />
          </div>
        </div>

        <div className="settings-button-text">
          <div className="item">
            <label className="item-label">{messages.commonButtonText}</label>
            <div
              className={`item-value ${
                showTranslationInputs ? 'item-value-with-translations' : ''
              }`}
            >
              <RichText
                field="email.buttonText"
                formatting={false}
                onFocus={handleFocus}
                singleLine={true}
                singleLineRemoveParagraph={true}
                templateTags={templateTags}
                templateTagsIdentifier="email-message-editor-button-text"
                withBorder={true}
              />
            </div>
          </div>

          {showTranslationInputs ? (
            <div className="item-translations">
              <LanguageVariants
                customProps={{
                  formatting: false,
                  onFocus: handleFocus,
                  placeholder: messages.commonAddTranslation,
                  singleLine: true,
                  singleLineRemoveParagraph: true,
                  templateTags: templateTags,
                  templateTagsIdentifier:
                    'email-message-editor-button-text-translations'
                }}
                field="email.buttonTextTranslations"
                supportedLocales={supportedTranslationLocales}
                type="richtext"
              />
            </div>
          ) : null}
        </div>

        <div className="settings-button-link">
          <div className="item">
            <label className="item-label">{messages.commonButtonLink}</label>
            <div
              className={`item-value ${
                showTranslationInputs ? 'item-value-with-translations' : ''
              }`}
            >
              <RichText
                field="email.buttonLink"
                formatting={false}
                onFocus={handleFocus}
                singleLine={true}
                singleLineRemoveParagraph={true}
                templateTags={templateTags}
                templateTagsIdentifier="email-message-editor-button-link"
                withBorder={true}
              />
            </div>
          </div>

          {showTranslationInputs ? (
            <div className="item-translations">
              <LanguageVariants
                customProps={{
                  formatting: false,
                  onFocus: handleFocus,
                  placeholder: messages.commonAddTranslation,
                  singleLine: true,
                  singleLineRemoveParagraph: true,
                  templateTags: templateTags,
                  templateTagsIdentifier:
                    'email-message-editor-button-link-translations'
                }}
                field="email.buttonLinkTranslations"
                supportedLocales={supportedTranslationLocales}
                type="richtext"
              />
            </div>
          ) : null}
        </div>
      </div>

      {templateTags?.length ? (
        <div className="template-tags">
          <div className="item">
            <label className="item-label">
              {messages.emailPlaceholders}

              <Tooltip
                childrenWrapInDiv={false}
                content={messages.emailPlaceholdersDescription}
              >
                <div className="item-label-description">
                  <Icon icon="question-circle" />
                </div>
              </Tooltip>
            </label>
            <div className="item-value">
              <div className="template-tags-content">
                {templateTags.map((templateTag) => (
                  <div className="template-tag" key={templateTag.key}>
                    <Button
                      category="lighter"
                      onClick={() => handleTemplateTagAdd(templateTag.key)}
                      size="header"
                      style={{ fontWeight: 600, padding: '3px 7px' }}
                      text={`${templateTag.label}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <style jsx>{`
        .email-message-editor {
          background-color: white;
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .item {
          display: flex;
          gap: 10px;
          padding: 0 15px;
        }
        .item-label {
          color: ${theme.textColorLight};
          display: flex;
          flex-grow: 0;
          flex-shrink: 0;
          font-size: 1.5rem;
          font-weight: 600;
          gap: 5px;
          padding: 14px 0px;
        }
        .item-label-description {
          font-size: 1.4rem;
          padding-top: 2px;
        }
        .item-value {
          flex-grow: 1;
          flex-shrink: 1;
          min-width: 0;
        }
        .item-translations {
          margin: -10px 0 10px;
          padding: 0 15px;
        }
        .item-value :global(.vuo-rich-text-editor) {
          margin: 9px 0;
          padding: 5px 10px;
        }
        .item-translations :global(.vuo-rich-text-editor) {
          padding: 5px 10px;
        }
        .header {
          display: flex;
          flex-direction: column;
          gap: 0px 15px;
        }
        .actions {
          display: flex;
          flex-shrink: 0;
          justify-content: space-between;
        }
        .select-template-input {
          padding-top: 9px;
          width: 200px;
        }
        .subject {
          border-top: 1px solid ${theme.lightGreyDark};
          flex-grow: 1;
          flex-shrink: 1;
        }
        .message {
          border-top: 1px solid ${theme.lightGreyDark};
          background-color: ${theme.lightGrey};
          flex-grow: 1;
          flex-shrink: 1;
          padding: 25px 15px;
        }
        .message-inner {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          font-weight: normal;
          margin: 0 auto;
          max-width: 570px;
          padding: 20px;
        }
        .message-rich-text,
        .message-button {
          font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
          -webkit-font-smoothing: auto;
          -moz-osx-font-smoothing: auto;
        }
        .message-heading .message-rich-text :global(.vuo-rich-text-editor),
        .message-heading
          .message-rich-text
          :global(.vuo-rich-text-editor .template-tag) {
          font-size: 19px;
          font-weight: 700;
        }
        .message-content,
        .message-content-after-button {
          margin: 15px 0;
        }
        .message-content .message-rich-text :global(.vuo-rich-text-editor),
        .message-content-after-button
          .message-rich-text
          :global(.vuo-rich-text-editor) {
          color: ${theme.textColorLight};
          font-size: 16px;
          line-height: 1.5;
        }
        .message-content-footnote {
          margin-top: 25px;
        }
        .message-content-footnote
          .message-rich-text
          :global(.vuo-rich-text-editor) {
          color: ${theme.textColorLight};
          font-size: 14px;
        }
        .message-button-container {
          margin: 20px 0;
          text-align: center;
        }
        .message-button {
          background-color: ${theme.mainColor};
          border-radius: ${theme.buttonBorderRadius};
          color: ${theme.mainTextColor};
          cursor: pointer;
          display: inline-block;
          font-size: 14px;
          font-weight: normal;
          padding: 12px 15px;
          text-decoration: none;
        }
        .settings-button {
          border-top: 1px solid ${theme.lightGreyDark};
          display: flex;
          flex-direction: column;
          gap: 0 20px;
        }
        .settings-button-enabled {
          padding: 14px 0;
        }
        .settings-button-text,
        .settings-button-link {
          flex-grow: 1;
          flex-shrink: 1;
          min-width: 0;
          width: 100%;
        }
        .settings-button-link {
          width: 130%;
        }
        .template-tags {
          border-top: 1px solid ${theme.lightGreyDark};
        }
        .template-tags-content {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          padding: 11px 10px;
        }
        .languages {
          display: flex;
          gap: 10px;
          margin: 17px 5px;
        }
        .language-settings {
          margin: 10px;
        }
        .language-settings-content {
          min-width: 200px;
          padding: 5px 0;
        }
        .language-settings :global(div[data-radix-popper-content-wrapper]) {
          z-index: 3 !important;
        }
        @media (min-width: ${breakpoints.huge}px) {
          .header {
            flex-direction: row-reverse;
          }
          .settings-button {
            flex-direction: row;
          }
          .subject {
            border-top: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default EmailMessageEditor;
